import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { createElement as _createElement } from "react";
import { referenceToOption as defaultReferenceToOption } from '../utils/referenceToOption';
import { forwardRef, useMemo, useEffect, useRef, useState, useCallback } from 'react';
import memoizeOne from 'react-utils/memoizeOne';
import UISelect from 'UIComponents/input/UISelect';
import { asHiddenProp } from 'UIComponents/utils/asHiddenProp';
import debounce from 'react-utils/debounce';
import allSettled from 'hs-promise-utils/allSettled';

function getUniqueOptions(options) {
  const hasValue = new Set();
  return options.filter(option => {
    if (!option) {
      return false;
    }

    if (hasValue.has(option.value)) {
      return false;
    }

    hasValue.add(option.value);
    return true;
  });
}

const idIsValid = id => {
  if (id === undefined || id === null || id === '') {
    return false;
  }

  return true;
};

const getMissingIds = memoizeOne((value, options) => {
  const valueArray = Array.isArray(value) ? value : [value];
  return valueArray.filter(idIsValid).filter(valueElement => !options.find(option => option && option.value === valueElement));
}); // Grab the values that could be successfully resolved and throw out any failed requests

const getFulfilledPromiseValues = promises => promises.reduce((acc, promise) => promise.status === 'fulfilled' ? [...acc, promise.value] : acc, []);

const loadOptions = (options, resolver, searchCount, searchOptionsCache, query, callback, pagination = {
  offset: 0
}, referenceToOption) => {
  const {
    offset
  } = pagination;
  const searchQuery = {
    count: searchCount,
    offset,
    query
  }; // Reset the search option cache if the pagination reset

  if (offset === 0) {
    searchOptionsCache.current = [];
  }

  resolver.api.search(searchQuery).then(({
    hasMore,
    offset: newOffset,
    references
  }) => {
    searchOptionsCache.current = searchOptionsCache.current.concat(references.map(referenceToOption));
    const allOptions = getUniqueOptions(options.concat(searchOptionsCache.current));
    callback(null, {
      options: allOptions,
      pagination: {
        hasMore,
        offset: newOffset
      }
    });
  }).catch(() => {});
};

// This is defined outside the component because if we set options to an empty
// array inside the component it constantly get re-created
const emptyArray = [];
const ReferenceLiteSearchSelect = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    autoload = true,
    filterOptions = false,
    ignoreCase = asHiddenProp(false),
    ignoreAccents = asHiddenProp(false),
    referenceToOption = defaultReferenceToOption,
    options = emptyArray,
    resolver,
    searchCount = 25,
    value
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["autoload", "filterOptions", "ignoreCase", "ignoreAccents", "referenceToOption", "options", "resolver", "searchCount", "value"]);

  const searchOptionsCache = useRef([]);
  const [valueOptions, setValueOptions] = useState([]);
  const resolverKey = useRef({
    resolver,
    key: 0
  }); // When `resolver` changes, bump `key` to force a re-mount of the `UISelect`
  // in order to clear its option and pagination caches. Otherwise, switching
  // to a new resolver which returns different references will continue showing
  // stale cached options from the previous resolver.
  //
  // NOTE: if resolvers exposed a cache key, we could use that instead. then
  // changing resolvers would only need to force a re-mount if the new resolver
  // had a different cache key (i.e. a different reference type or parameters)

  if (resolver !== resolverKey.current.resolver) {
    searchOptionsCache.current = [];
    resolverKey.current.resolver = resolver;
    resolverKey.current.key = resolverKey.current.key + 1;
  }

  useEffect(() => {
    const allOptions = options.concat(searchOptionsCache.current);
    const missingIds = getMissingIds(value, allOptions);

    if (missingIds.length === 0) {
      return;
    }

    const fetchPromises = missingIds.map(missingId => {
      return resolver.api.byId(missingId);
    });
    allSettled(fetchPromises).then(settledPromises => {
      const validValues = getFulfilledPromiseValues(settledPromises);
      setValueOptions(validValues.map(referenceToOption));
    }).catch(() => {});
  }, [options, value, resolver, referenceToOption]);
  const debouncedLoadOptions = useMemo(() => debounce((query, callback, pagination) => loadOptions(options, resolver, searchCount, searchOptionsCache, query, callback, pagination, referenceToOption), 500), [options, resolver, searchCount, referenceToOption]);
  const handleLoadOptions = useCallback((query, callback, pagination) => {
    // We don't debounce non-search queries in order to work around a race
    // condition: https://git.hubteam.com/HubSpot/UIComponents/issues/10718
    // that happens with async loadOptions and automatically opened
    // UISelects.
    if (!query) {
      loadOptions(options, resolver, searchCount, searchOptionsCache, query, callback, pagination, referenceToOption);
      return;
    }

    debouncedLoadOptions(query, callback, pagination);
  }, [debouncedLoadOptions, options, resolver, searchCount, referenceToOption]); // Options passed to the wrapped `<UISelect options={...} />`
  //
  // The UISelect `options` prop doesn't control what options are visible in
  // the dropdown when async mode is on (`loadOptions`), it just allows us to
  // resolve options that are currently used in `value`. This makes it so we
  // can resolve value options into human-readable labels before opening the
  // dropdown, it does not make the option appear in the dropdown (that is
  // handled by including `options` in the `loadOptions` callback above).
  // It is a union of two lists:
  //
  // `options`      - `options` prop passed from the parent. options that the
  //                  parent is aware of, separate from any options fetched from RRL
  // `valueOptions` - options fetched dynamically from RRL for each `value`
  //                  that is not present in `options`, so labels can be shown
  //                  before the dropdown is opened, and for options not in the
  //                  first page of search results

  const uiselectOptions = useMemo(() => getUniqueOptions([...options, ...valueOptions]), [options, valueOptions]);
  return /*#__PURE__*/_createElement(UISelect, Object.assign({}, rest, {
    autoload: autoload,
    anchorType: "button",
    filterOptions: filterOptions,
    ignoreCase: ignoreCase,
    ignoreAccents: ignoreAccents,
    key: resolverKey.current.key,
    loadOptions: handleLoadOptions,
    options: uiselectOptions,
    ref: ref,
    value: value
  }));
});
ReferenceLiteSearchSelect.displayName = 'ReferenceLiteSearchSelect';
export default ReferenceLiteSearchSelect;